<template>
    <v-container fluid style="max-width: 640px">
        <h2 class="text-h6 mb-1">Profilo</h2>
        <div class="text-body-2 text-medium-emphasis mb-4">Gestisci i tuoi dati e la tua password</div>

        <v-card class="mb-4" variant="outlined">
            <v-card-title>I tuoi dati</v-card-title>
            <v-card-text>
                <v-form ref="formDatiRef">
                    <v-text-field :model-value="username" label="Username" disabled hide-details class="mb-4" />
                    <v-text-field v-model="formDati.displayName" label="Nome visualizzato" class="mb-4" />
                    <v-text-field v-model="formDati.eMail" label="Email" type="email" />
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <v-alert v-if="messaggioDati" type="success" density="compact" variant="tonal" class="mr-2 py-1">
                    {{ messaggioDati }}
                </v-alert>
                <v-btn color="primary" :loading="salvandoDati" @click="salvaDati">Salva</v-btn>
            </v-card-actions>
        </v-card>

        <v-card variant="outlined">
            <v-card-title>Cambia password</v-card-title>
            <v-card-text>
                <v-form ref="formPasswordRef">
                    <v-text-field v-model="formPassword.passwordAttuale" type="password" label="Password attuale"
                        :rules="[v => !!v || 'Obbligatorio']" class="mb-4" />
                    <v-text-field v-model="formPassword.nuovaPassword" type="password" label="Nuova password"
                        :rules="[v => (!!v && v.length >= 8) || 'Almeno 8 caratteri']" class="mb-4" />
                    <v-text-field v-model="formPassword.confermaPassword" type="password" label="Conferma nuova password"
                        :rules="[v => v === formPassword.nuovaPassword || 'Le password non coincidono']" />
                </v-form>
                <v-alert v-if="erroreConfermaPassword" type="error" density="compact" variant="tonal" class="mt-3">
                    {{ erroreConfermaPassword }}
                </v-alert>
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <v-alert v-if="messaggioPassword" type="success" density="compact" variant="tonal" class="mr-2 py-1">
                    {{ messaggioPassword }}
                </v-alert>
                <v-btn color="primary" :loading="salvandoPassword" @click="cambiaPassword">Cambia password</v-btn>
            </v-card-actions>
        </v-card>

        <v-card v-if="role === 'Admin'" variant="outlined" class="mt-4">
            <v-card-title>Link di login automatico</v-card-title>
            <v-card-text>
                <p class="text-body-2 text-medium-emphasis mb-4">
                    Apre il planner senza barra degli strumenti né menu laterale, già loggato come il tuo account.
                    Da usare solo per integrare pagine del planner in una webview di un sistema esterno affidabile:
                    chi possiede il link accede come te senza password.
                </p>
                <v-text-field v-model="embedRedirectPath" label="Pagina di destinazione" density="compact"
                    hint="Es. /calendario" persistent-hint class="mb-2" />

                <template v-if="embedToken">
                    <v-text-field :model-value="embedUrl" label="Link" readonly density="compact">
                        <template #append-inner>
                            <v-icon size="small" style="cursor:pointer" @click="copiaEmbedUrl"
                                title="Copia link">mdi-content-copy</v-icon>
                        </template>
                    </v-text-field>
                    <p class="text-caption text-medium-emphasis">
                        Per una pagina diversa non serve un nuovo link: cambia solo il campo "Pagina di
                        destinazione" sopra e copia di nuovo l'URL, il token resta lo stesso.
                    </p>
                </template>
                <template v-else>
                    <p class="text-body-2">Nessun link generato per il tuo account.</p>
                </template>
            </v-card-text>
            <v-card-actions>
                <v-btn v-if="embedToken" variant="text" color="error" @click="revocaLinkEmbed">Revoca</v-btn>
                <v-spacer />
                <v-btn color="primary" @click="generaLinkEmbed">{{ embedToken ? 'Rigenera' : 'Genera link' }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-container>
</template>

<script>
export default {
    name: "ProfiloPage",

    data() {
        return {
            formDati: { displayName: "", eMail: "" },
            salvandoDati: false,
            messaggioDati: "",

            formPassword: { passwordAttuale: "", nuovaPassword: "", confermaPassword: "" },
            salvandoPassword: false,
            messaggioPassword: "",
            erroreConfermaPassword: "",

            embedRedirectPath: "/calendario",
        };
    },

    computed: {
        username() {
            return this.$store.getters["user/getNome"];
        },
        mail() {
            return this.$store.getters["user/getMail"];
        },
        displayName() {
            return this.$store.getters["user/getDisplayName"];
        },
        role() {
            return this.$store.getters["user/getRole"];
        },
        embedToken() {
            return this.$store.getters["user/getEmbedToken"];
        },
        embedUrl() {
            if (!this.embedToken) return "";
            const redirect = this.embedRedirectPath || "/";
            return `${window.location.origin}/embed-login?token=${this.embedToken}&redirect=${encodeURIComponent(redirect)}`;
        },
    },

    async mounted() {
        try {
            await this.$store.dispatch("user/userInfo");
        } catch (error) {
            console.log(error);
        }
        this.formDati = { displayName: this.displayName, eMail: this.mail };
    },

    methods: {
        async salvaDati() {
            if (!this.$refs.formDatiRef.validate()) return;
            this.messaggioDati = "";
            this.salvandoDati = true;
            try {
                await this.$store.dispatch("user/aggiornaProfilo", { ...this.formDati });
                this.messaggioDati = "Dati aggiornati.";
            } catch (error) {
                console.log(error);
            }
            this.salvandoDati = false;
        },

        async cambiaPassword() {
            this.erroreConfermaPassword = "";
            this.messaggioPassword = "";
            if (!this.$refs.formPasswordRef.validate()) return;

            this.salvandoPassword = true;
            try {
                await this.$store.dispatch("user/cambiaPassword", {
                    passwordAttuale: this.formPassword.passwordAttuale,
                    nuovaPassword: this.formPassword.nuovaPassword,
                });
                this.messaggioPassword = "Password aggiornata.";
                this.formPassword = { passwordAttuale: "", nuovaPassword: "", confermaPassword: "" };
                this.$refs.formPasswordRef.resetValidation();
            } catch (error) {
                this.erroreConfermaPassword = "Password attuale non corretta.";
                console.log(error);
            }
            this.salvandoPassword = false;
        },

        async generaLinkEmbed() {
            try {
                await this.$store.dispatch("user/generaEmbedTokenMio");
            } catch (error) {
                console.log(error);
            }
        },

        async revocaLinkEmbed() {
            try {
                await this.$store.dispatch("user/revocaEmbedTokenMio");
            } catch (error) {
                console.log(error);
            }
        },

        async copiaEmbedUrl() {
            try {
                await navigator.clipboard.writeText(this.embedUrl);
            } catch (error) {
                console.log(error);
            }
        },
    },
};
</script>
